import React, { createContext, useContext, useMemo, useState } from 'react'

/** Whether a routed sidebar item is the current page: its own route or a
 * drill-in under it (`/tenant-admin/my-policies/Q-1`), ignoring the query
 * and hash (`?lang=en`). */
export function isSidebarRouteActive(asPath: string, href: string): boolean {
	const path = asPath.split(/[?#]/)[0].replace(/\/+$/, '') || '/'
	const target = href.replace(/\/+$/, '') || '/'
	return path === target || path.startsWith(`${target}/`)
}

export interface DashboardSidebarItem {
	id: string
	label: string
	icon: string
	// Present for real routed pages (the sponsor multi-product portal) —
	// Layout.tsx renders these as Link-based nav with asPath-driven active
	// state instead of the same-page anchor-scroll SectionNavItem used by
	// the single-page customer/sponsor dashboards.
	href?: string
	// Small unread/needs-attention count badge next to the item's label
	// (e.g. the sponsor portal's Orders item — pending orders awaiting
	// completion/payment). Omitted or 0 renders no badge.
	badgeCount?: number
}

interface DashboardSidebarContextValue {
	items: DashboardSidebarItem[]
	activeId: string | null
	setItems: (items: DashboardSidebarItem[]) => void
	setActiveId: (id: string | null) => void
}

const DashboardSidebarContext = createContext<DashboardSidebarContextValue>({
	items: [],
	activeId: null,
	setItems: () => {},
	setActiveId: () => {},
})

/** Wraps the whole app (see pages/_app.tsx) so the Sidebar inside Layout.tsx
 * and whichever page is currently rendered (e.g. pages/dashboard/index.tsx)
 * can share sidebar nav state despite being siblings under <Layout>, not
 * parent/child — Layout renders {children} and the Sidebar side by side. */
export function DashboardSidebarProvider({
	children,
}: {
	children: React.ReactNode
}) {
	const [items, setItems] = useState<DashboardSidebarItem[]>([])
	const [activeId, setActiveId] = useState<string | null>(null)
	const value = useMemo(
		() => ({ items, activeId, setItems, setActiveId }),
		[items, activeId]
	)
	return (
		<DashboardSidebarContext.Provider value={value}>
			{children}
		</DashboardSidebarContext.Provider>
	)
}

export function useDashboardSidebar() {
	return useContext(DashboardSidebarContext)
}
